import { generateId } from './helpers.js';

// Mock Brands
export const mockBrands = [
    {
        id: generateId(),
        name: 'TechFlow',
        logo: '🚀',
        industry: 'Technology',
        voice: {
            tone: 'Professional yet approachable',
            personality: 'Innovative, forward-thinking, customer-focused',
            keyMessages: ['Simplifying complexity', 'Innovation that matters', 'Built for the future'],
        },
        visual: {
            primaryColor: '#7C3AED',
            secondaryColor: '#06B6D4',
            accentColor: '#EC4899',
            fonts: {
                heading: 'Outfit',
                body: 'Inter',
            },
        },
        audience: {
            demographics: '25-45 years old, tech-savvy professionals',
            interests: ['Technology', 'Innovation', 'Productivity'],
            painPoints: ['Complex workflows', 'Data silos', 'Manual processes'],
        },
        guidelines: 'Always emphasize simplicity and user experience. Avoid technical jargon when possible.',
        createdAt: '2024-01-15',
    },
    {
        id: generateId(),
        name: 'EcoLiving',
        logo: '🌿',
        industry: 'Sustainable Products',
        voice: {
            tone: 'Warm, inspiring, authentic',
            personality: 'Passionate, eco-conscious, community-oriented',
            keyMessages: ['Sustainable living made easy', 'For people and planet', 'Every choice matters'],
        },
        visual: {
            primaryColor: '#10B981',
            secondaryColor: '#059669',
            accentColor: '#F59E0B',
            fonts: {
                heading: 'Outfit',
                body: 'Inter',
            },
        },
        audience: {
            demographics: '28-50 years old, environmentally conscious consumers',
            interests: ['Sustainability', 'Wellness', 'Organic products'],
            painPoints: ['Finding truly sustainable products', 'Greenwashing', 'High prices'],
        },
        guidelines: 'Always highlight environmental impact. Use natural imagery and earth tones.',
        createdAt: '2024-02-20',
    },
];

// Mock Creatives
export const mockCreatives = [
    {
        id: generateId(),
        brandId: mockBrands[0].id,
        brandName: mockBrands[0].name,
        title: 'Product Launch - AI Dashboard',
        format: 'social-post',
        platforms: ['instagram', 'facebook', 'linkedin'],
        imageUrl: null,
        copy: 'Transform your data into insights with our new AI-powered dashboard. 🚀 #TechFlow #AI',
        prompt: 'Modern tech dashboard UI with purple gradient, floating data visualizations, clean design',
        status: 'scheduled',
        scheduledDate: '2024-12-25T10:00:00',
        createdAt: '2024-12-20',
        createdBy: 'Sarah Chen',
    },
    {
        id: generateId(),
        brandId: mockBrands[1].id,
        brandName: mockBrands[1].name,
        title: 'Holiday Campaign - Gift Sets',
        format: 'display-ad',
        platforms: ['google', 'facebook'],
        imageUrl: null,
        copy: 'Give the gift of sustainability this season 🌿',
        prompt: 'Organic gift basket with natural products, warm lighting, eco-friendly packaging',
        status: 'posted',
        scheduledDate: '2024-12-20T08:00:00',
        createdAt: '2024-12-18',
        createdBy: 'Mike Johnson',
    },
];

// Mock Analytics Data
export const mockAnalytics = {
    overview: {
        impressions: { current: 1250000, previous: 980000 },
        clicks: { current: 45000, previous: 38000 },
        conversions: { current: 3200, previous: 2800 },
        spend: { current: 12500, previous: 11000 },
        revenue: { current: 89000, previous: 72000 },
    },
    platformPerformance: [
        {
            platform: 'Facebook',
            impressions: 520000,
            clicks: 18500,
            ctr: 3.56,
            conversions: 1250,
            spend: 4800,
            roas: 7.8,
        },
        {
            platform: 'Instagram',
            impressions: 385000,
            clicks: 15200,
            ctr: 3.95,
            conversions: 980,
            spend: 3200,
            roas: 9.2,
        },
        {
            platform: 'LinkedIn',
            impressions: 185000,
            clicks: 7400,
            ctr: 4.0,
            conversions: 620,
            spend: 2900,
            roas: 6.5,
        },
        {
            platform: 'Google',
            impressions: 160000,
            clicks: 3900,
            ctr: 2.44,
            conversions: 350,
            spend: 1600,
            roas: 8.1,
        },
    ],
    performanceTrend: [
        { date: '2024-12-01', impressions: 38000, clicks: 1400, conversions: 95 },
        { date: '2024-12-05', impressions: 42000, clicks: 1580, conversions: 112 },
        { date: '2024-12-10', impressions: 46000, clicks: 1720, conversions: 128 },
        { date: '2024-12-15', impressions: 51000, clicks: 1950, conversions: 145 },
        { date: '2024-12-20', impressions: 48000, clicks: 1840, conversions: 138 },
    ],
    topCreatives: [
        {
            id: mockCreatives[0].id,
            title: mockCreatives[0].title,
            impressions: 125000,
            clicks: 5200,
            ctr: 4.16,
            conversions: 380,
            spend: 1200,
            revenue: 11400,
            roas: 9.5,
        },
        {
            id: mockCreatives[1].id,
            title: mockCreatives[1].title,
            impressions: 98000,
            clicks: 3850,
            ctr: 3.93,
            conversions: 290,
            spend: 950,
            revenue: 8200,
            roas: 8.6,
        },
    ],
};

// Mock AI Insights
export const mockInsights = [
    {
        id: generateId(),
        type: 'creative-performance',
        priority: 'high',
        title: 'Video ads outperforming static images',
        description: 'Your video creatives are generating 3.2x higher engagement than static images. Consider allocating more budget to video production.',
        impact: '+$2,300 projected monthly revenue',
        confidence: 92,
        actionItems: [
            'Increase video creative production by 40%',
            'Test short-form video on Instagram Reels',
            'Repurpose top-performing static ads as videos',
        ],
        createdAt: '2024-12-20',
    },
    {
        id: generateId(),
        type: 'timing-optimization',
        priority: 'medium',
        title: 'Optimal posting times identified',
        description: 'Your audience engages 45% more on Tuesdays between 6-8 PM EST and Thursdays at 10 AM EST.',
        impact: '+680 conversions/month',
        confidence: 88,
        actionItems: [
            'Reschedule low-performing posts to peak times',
            'Set up automated posting for Tuesday evenings',
        ],
        createdAt: '2024-12-19',
    },
    {
        id: generateId(),
        type: 'budget-optimization',
        priority: 'high',
        title: 'LinkedIn showing higher conversion rates',
        description: 'LinkedIn campaigns have 25% better conversion rates than other platforms for your B2B audience. Recommended budget reallocation.',
        impact: '+$1,850 monthly revenue',
        confidence: 91,
        actionItems: [
            'Increase LinkedIn budget by 20% ($580/month)',
            'Reduce Facebook budget by 10% ($480/month)',
            'Test LinkedIn video ads',
        ],
        createdAt: '2024-12-18',
    },
    {
        id: generateId(),
        type: 'audience-insight',
        priority: 'medium',
        title: 'Age group 25-34 most responsive',
        description: 'The 25-34 age demographic shows 60% higher engagement and 2x conversion rate compared to other segments.',
        impact: 'Refine targeting for 35% efficiency gain',
        confidence: 85,
        actionItems: [
            'Create age-specific creative variations',
            'Adjust audience targeting weights',
        ],
        createdAt: '2024-12-17',
    },
];

// Mock Team Members
export const mockTeam = [
    {
        id: generateId(),
        name: 'Sarah Chen',
        email: 'sarah@company.com',
        role: 'Admin',
        avatar: null,
        brands: [mockBrands[0].id, mockBrands[1].id],
        lastActive: '2024-12-21T14:30:00',
        joinedAt: '2024-01-10',
    },
    {
        id: generateId(),
        name: 'Mike Johnson',
        email: 'mike@company.com',
        role: 'Creator',
        avatar: null,
        brands: [mockBrands[1].id],
        lastActive: '2024-12-21T11:15:00',
        joinedAt: '2024-02-15',
    },
    {
        id: generateId(),
        name: 'Emily Rodriguez',
        email: 'emily@company.com',
        role: 'Reviewer',
        avatar: null,
        brands: [mockBrands[0].id],
        lastActive: '2024-12-20T16:45:00',
        joinedAt: '2024-03-20',
    },
];

// Mock Activity Log
export const mockActivityLog = [
    {
        id: generateId(),
        user: 'Sarah Chen',
        action: 'created',
        target: 'Creative: Product Launch - AI Dashboard',
        timestamp: '2024-12-20T14:30:00',
    },
    {
        id: generateId(),
        user: 'Mike Johnson',
        action: 'scheduled',
        target: 'Post on Instagram, Facebook',
        timestamp: '2024-12-20T11:20:00',
    },
    {
        id: generateId(),
        user: 'Emily Rodriguez',
        action: 'approved',
        target: 'Creative: Holiday Campaign',
        timestamp: '2024-12-19T16:15:00',
    },
    {
        id: generateId(),
        user: 'Sarah Chen',
        action: 'updated',
        target: 'Brand: TechFlow - Voice guidelines',
        timestamp: '2024-12-19T09:45:00',
    },
];

// Mock Scheduled Posts
export const mockScheduledPosts = [
    {
        id: generateId(),
        creativeId: mockCreatives[0].id,
        creative: mockCreatives[0],
        platforms: ['instagram', 'facebook', 'linkedin'],
        scheduledDate: '2024-12-25T10:00:00',
        timezone: 'EST',
        status: 'scheduled',
    },
    {
        id: generateId(),
        creativeId: generateId(),
        creative: {
            title: 'New Year Campaign',
            imageUrl: null,
            copy: 'Start 2025 with sustainable choices 🌱',
        },
        platforms: ['instagram', 'tiktok'],
        scheduledDate: '2025-01-01T08:00:00',
        timezone: 'EST',
        status: 'scheduled',
    },
];

// Mock Automation Settings
export const mockAutomationSettings = {
    enabled: false,
    budgetAutonomy: {
        enabled: false,
        maxDailyChange: 15,
        monthlyBudgetCap: 10000,
        requireApprovalOver: 500,
    },
    creativeAutonomy: {
        enabled: false,
        canGenerateVariations: true,
        canCreateNewConcepts: false,
        requiresReview: true,
    },
    platformAutonomy: {
        instagram: { enabled: false, autonomyLevel: 'low' },
        facebook: { enabled: false, autonomyLevel: 'medium' },
        linkedin: { enabled: false, autonomyLevel: 'low' },
        google: { enabled: false, autonomyLevel: 'low' },
    },
    performanceThresholds: {
        pauseIfCTRBelow: 1.5,
        pauseIfROASBelow: 2.0,
        scaleIfROASAbove: 5.0,
    },
};

// Mock AI Decisions Log
export const mockAIDecisions = [
    {
        id: generateId(),
        timestamp: '2024-12-21T10:30:00',
        type: 'budget-adjustment',
        action: 'Increased LinkedIn budget by $120 (15%)',
        reason: 'ROAS above 8.0 threshold for 3 consecutive days',
        impact: '+$960 projected revenue',
        status: 'executed',
        canRevert: true,
    },
    {
        id: generateId(),
        timestamp: '2024-12-21T09:15:00',
        type: 'creative-test',
        action: 'Generated 2 variations of Holiday Campaign',
        reason: 'Original creative CTR declining',
        impact: 'Testing in progress',
        status: 'in-progress',
        canRevert: false,
    },
    {
        id: generateId(),
        timestamp: '2024-12-20T16:00:00',
        type: 'schedule-optimization',
        action: 'Rescheduled 3 posts to Tuesday 6 PM',
        reason: 'Historical data shows 45% higher engagement',
        impact: '+68 projected conversions',
        status: 'pending-approval',
        canRevert: true,
    },
];

export const currentUser = {
    id: generateId(),
    name: 'Sarah Chen',
    email: 'sarah@company.com',
    role: 'Admin',
    avatar: null,
};
